import React, { useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { severities, allergy_types } from "../enums";

const purple = "#2E2868";


const AllergiesSubsection = ({
  subjectId,
  isCreate,
  form,
  allergies,
  setAllergies,
  isOpen,
  onToggle,
  saveSubject,
  loadSubject,
  getAllergies,
}) => {
  const [showAddAllergy, setShowAddAllergy] = useState(false);
  const [editingAllergyIndex, setEditingAllergyIndex] = useState(null);

  const [allergyOptions, setAllergyOptions] = useState([]);
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const [newAllergy, setNewAllergy] = useState({
    id: null,
    allergy_id: null, // null => custom
    allergy_name: "",
    allergy_type: "",
    severity: "",
    reaction: "",
    notes: "",
  });

  // --- Load allergies options (NO duplicate custom option) ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await getAllergies();
      if (!mounted) return;
      setAllergyOptions(res || []);
    })();
    return () => {
      mounted = false;
    };
  }, [getAllergies]);

  const handleAllergyChange = (field, val) =>
    setNewAllergy((prev) => ({ ...prev, [field]: val }));

  const resetForm = () => {
    setNewAllergy({
      id: null,
      allergy_id: null,
      allergy_name: "",
      allergy_type: "",
      severity: "",
      reaction: "",
      notes: "",
    });
    setEditingAllergyIndex(null);
    setShowAddAllergy(false);
    setIsCustomSelected(false);
  };

  // --- Helpers: safe id matching ---
  const findAllergyById = (rawId) => {
    if (rawId === "" || rawId == null) return null;

    if (rawId === "__CUSTOM__") return { id: null, name: "Custom Allergy" };

    return (allergyOptions || []).find((a) => String(a?.id) === String(rawId)) || null;
  };

  // options:
  // - custom (id null) => "__CUSTOM__"
  // - others => String(id)
  const allergySelectOptions = useMemo(() => {
    return (allergyOptions || []).map((a) => {
      const isCustom = a?.id == null;
      return {
        value: isCustom ? "__CUSTOM__" : String(a.id),
        label: a?.name ?? String(a?.id ?? ""),
        isCustom,
        raw: a,
      };
    });
  }, [allergyOptions]);

  const selectedAllergyValue = useMemo(() => {
    if (isCustomSelected) return "__CUSTOM__";
    return newAllergy.allergy_id == null ? "" : String(newAllergy.allergy_id);
  }, [isCustomSelected, newAllergy.allergy_id]);

  const syncFromFreshSubject = (fresh) => {
    if (!fresh?.subject_allergies) return;

    setAllergies(
      fresh.subject_allergies.map((a) => ({
        id: a.id,
        allergy_id: a.allergy_id ?? null,
        allergy_name:
          a.allergy_name ||
          a.allergy_id_name ||
          (a.allergy && a.allergy.name) ||
          "",
        allergy_type: a.allergy_type ?? "",
        severity: a.severity ?? "",
        reaction: a.reaction ?? "",
        notes: a.notes ?? "",
      }))
    );
  };

  const saveAllergy = async () => {
    const hasAllergy = newAllergy.allergy_id != null || !!newAllergy.allergy_name?.trim();
    if (!hasAllergy || !newAllergy.severity) return;

    const payloadObject = {
      ...(newAllergy.id && { id: newAllergy.id }),
      allergy_id: newAllergy.allergy_id ?? null,
      allergy_name: newAllergy.allergy_id != null ? null : newAllergy.allergy_name.trim(),
      allergy_type: (newAllergy.allergy_type || "").trim() || null,
      severity: newAllergy.severity,
      reaction: (newAllergy.reaction || "").trim() || null,
      notes: (newAllergy.notes || "").trim() || null,
    };

    const updatedList =
      editingAllergyIndex !== null
        ? allergies.map((item, idx) => (idx === editingAllergyIndex ? { ...item, ...payloadObject } : item))
        : [...allergies, payloadObject];

    // Create mode -> client-side only
    if (isCreate && form?.setValue) {
      setAllergies(updatedList);
      form.setValue({ key: "subject_allergies", value: updatedList });
      resetForm();
      return;
    }

    // Edit mode -> save to backend
    await saveSubject({
      input: {
        subjects: { id: subjectId },
        subject_allergies: [payloadObject],
      },
    });

    //  Force reload to bypass stale rootContext cache
    const fresh = await loadSubject({ force: true });
    syncFromFreshSubject(fresh);

    resetForm();
  };

  const deleteCurrentAllergy = async () => {
    if (isCreate || editingAllergyIndex === null) return;

    const record = allergies[editingAllergyIndex];
    if (!record?.id) return;

    await saveSubject({
      input: { subjects: { id: subjectId } },
      input_delete: [{ id: record.id, key: "subject_allergies" }],
    });

    // ✅ Force reload
    const fresh = await loadSubject({ force: true });
    syncFromFreshSubject(fresh);

    resetForm();
  };

  return (
    <div className="mb-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center" onClick={onToggle} style={{ cursor: "pointer" }}>
          <i
            className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
            style={{ color: purple, fontSize: "14px", marginRight: "6px" }}
          />
          <h6 className="fw-bold m-0" style={{ color: purple }}>
            Allergies ({allergies.length})
          </h6>
        </div>

        <span
          onClick={() => {
            setShowAddAllergy(true);
            setEditingAllergyIndex(null);
            setNewAllergy({
              id: null,
              allergy_id: null,
              allergy_name: "",
              allergy_type: "",
              severity: "",
              reaction: "",
              notes: "",
            });
            setIsCustomSelected(false);
          }}
          style={{ fontSize: "13px", color: purple, cursor: "pointer", fontWeight: 600 }}
        >
          + Add
        </span>
      </div>

      {/* FORM */}
      {showAddAllergy && (
        <div className="p-3 mb-3 position-relative" style={{ background: "#f2f2f7", borderRadius: "12px" }}>
          <span
            onClick={() => setShowAddAllergy(false)}
            style={{
              position: "absolute",
              right: "12px",
              top: "10px",
              cursor: "pointer",
              color: purple,
              fontWeight: "bold",
              fontSize: "23px",
            }}
          >
            ×
          </span>

          {/* Allergy */}
          <div className="mb-2">
            <label className="mb-1">Allergy</label>
            <select
              value={selectedAllergyValue}
              onChange={(e) => {
                const val = e.target.value;

                if (val === "__CUSTOM__") {
                  setIsCustomSelected(true);
                  setNewAllergy((prev) => ({
                    ...prev,
                    allergy_id: null,
                    allergy_name: "",
                  }));
                  return;
                }

                const selected = findAllergyById(val);
                setIsCustomSelected(false);
                setNewAllergy((prev) => ({
                  ...prev,
                  allergy_id: selected?.id ?? null,
                  allergy_name: selected?.name ?? "",
                }));
              }}
              className="form-control rounded-pill border-0"
            >
              <option value="">Select allergy</option>
              {allergySelectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Allergy Name */}
          {isCustomSelected && (
            <div className="mb-2">
              <label className="mb-1">Custom Allergy Name</label>
              <input
                value={newAllergy.allergy_name}
                onChange={(e) => handleAllergyChange("allergy_name", e.target.value)}
                className="form-control rounded-pill border-0"
                placeholder="Enter allergy name"
              />
            </div>
          )}

          {/* Type */}
          <div className="mb-2">
            <label className="mb-1">Type</label>
            <select
              value={newAllergy.allergy_type ?? ""}
              onChange={(e) => handleAllergyChange("allergy_type", e.target.value)}
              className="form-control rounded-pill border-0"
            >
              <option value="">Select Type</option>
              {(allergy_types || []).map((t) => {
                const val = t.id || t;
                const label = t.label || t.id || t;
                return (
                  <option key={val} value={val}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Severity */}
          <div className="mb-2">
            <label className="mb-1">Severity</label>
            <select
              value={newAllergy.severity ?? ""}
              onChange={(e) => handleAllergyChange("severity", e.target.value)}
              className="form-control rounded-pill border-0"
            >
              <option value="">Unspecified</option>
              {severities.map((s) => (
                <option key={s.label} value={s.id ?? ""}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* Reaction */}
          <div className="mb-2">
            <label className="mb-1">Reaction Description</label>
            <textarea
              rows={2}
              value={newAllergy.reaction}
              onChange={(e) => handleAllergyChange("reaction", e.target.value)}
              className="form-control rounded-3 border-0"
            />
          </div>

          {/* Notes */}
          <div className="mb-3">
            <label className="mb-1">Notes</label>
            <textarea
              rows={2}
              value={newAllergy.notes}
              onChange={(e) => handleAllergyChange("notes", e.target.value)}
              className="form-control rounded-3 border-0"
            />
          </div>

          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-sm"
              onClick={saveAllergy}
              style={{ background: purple, color: "#fff", borderRadius: "20px", padding: "4px 18px" }}
            >
              Save
            </button>

            {!isCreate && editingAllergyIndex !== null && (
              <button
                className="btn btn-sm"
                onClick={deleteCurrentAllergy}
                style={{ background: "#c0392b", color: "#fff", borderRadius: "20px", padding: "4px 18px" }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* LIST */}
      {isOpen &&
        allergies.map((item, idx) => (
          <div
            key={item.id ?? idx}
            className="d-flex justify-content-between align-items-center mb-2"
            style={{ fontSize: "14px", gap: "8px" }}
          >
            <div>
              <div>
                {item.allergy_name} | {item.severity}
              </div>
              <div style={{ fontSize: "12px", color: "#777" }}>
                {item.reaction || item.notes || "Allergy details"}
              </div>
            </div>

            {!isCreate && (
              <FontAwesomeIcon
                icon={faPen}
                style={{ cursor: "pointer", color: purple, fontSize: "14px" }}
                onClick={() => {
                  setNewAllergy({
                    id: item.id ?? null,
                    allergy_id: item.allergy_id ?? null,
                    allergy_name: item.allergy_name ?? "",
                    allergy_type: item.allergy_type ?? "",
                    severity: item.severity ?? "",
                    reaction: item.reaction ?? "",
                    notes: item.notes ?? "",
                  });

                  setEditingAllergyIndex(idx);
                  setShowAddAllergy(true);

                  // custom if no allergy_id but has name
                  setIsCustomSelected(item.allergy_id == null && !!item.allergy_name);
                }}
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default AllergiesSubsection;